// Editor "Personalizar meu painel" da lateral — extraído de
// `chat-sidebar.vue` (review do PR #2303). O RASCUNHO staged, a troca de
// modo com fade único e o fechamento depois do "Salvar" moram aqui; o
// template continua no componente.
//
// Contrato com quem usa o mixin (hoje só `chat-sidebar.vue`): o componente
// fornece `recolhida` (data) e `alternarRecolhida()` (método) — o editor
// não cabe na lateral recolhida e precisa abri-la/sair quando ela recolhe.
// Espelha `$duracao-da-troca-de-modo` do `<style>`: é quanto o corpo leva
// pra esmaecer antes de o modo trocar (e o mesmo pra voltar).
const DURACAO_DA_TROCA_DE_MODO_MS = 160
// Rede de segurança de `trocarModoComFade`: se o `@after-enter` do painel
// não vier (painel que não trocou), o corpo volta sozinho depois disto.
const REDE_DA_TROCA_DE_MODO_MS = 800

export default {
  props: {
    // Trava do botão "Personalizar meu painel" — decidida pelo PAI
    // (`PERSONALIZACAO_PAINEL_ATIVA`, `constants/Feature.js`). Mesmo arranjo
    // de `buscaGlobalDisponivel`: esta sidebar não lê a constante direto.
    personalizacaoDisponivel: {
      type: Boolean,
      default: false,
    },
    // Editor de personalização aberto — troca o painel de insights pela
    // paleta + canvas, com prioridade máxima na cadeia de `v-if`.
    modoPersonalizacao: {
      type: Boolean,
      default: false,
    },
    // `[{id, rotulo, rotuloCompleto, icone, variante, porLinha}]` — módulos
    // visíveis, na ordem escolhida. Fonte do RASCUNHO do editor (ver
    // `personalizacaoDraftVisiveis`) — esta sidebar não persiste nada
    // sozinha, só edita local até "Salvar".
    itensPersonalizaveisVisiveis: {
      type: Array,
      default: () => [],
    },
    // Catálogo INTEIRO (visíveis + ocultos), já filtrado por permissão/
    // feature flag do escritório pelo PAI — mesma disciplina de
    // `itensPersonalizaveisVisiveis`. Usado para: (a) derivar os OCULTOS da
    // paleta (complemento do rascunho) e (b) "Restaurar padrão" (todos
    // visíveis, na ordem do catálogo).
    itensPersonalizaveisCatalogo: {
      type: Array,
      default: () => [],
    },
    // "Salvar" em voo no pai (PUT/DELETE) — `loading` do botão. Quando cai
    // com o rascunho já igual à preferência salva, o editor fecha.
    salvandoPersonalizacao: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // RASCUNHO do editor de personalização — edição local, staged: só vira
      // preferência de verdade (evento `salvar-personalizacao`) quando o
      // usuário clica "Salvar". Sincronizado a partir de
      // `itensPersonalizaveisVisiveis` sempre que o editor ABRE (ver watch de
      // `modoPersonalizacao`) — nunca durante a edição, senão cada gesto do
      // usuário seria pisado pela prop.
      personalizacaoDraftVisiveis: [],
      // Entrar/sair do editor: o corpo inteiro esmaece, o modo troca "no
      // escuro" e o corpo volta — UMA animação, não uma por peça (faixa,
      // rodapé, saldo, canvas trocando cada um no seu tempo davam um
      // pisca-pisca; screencast do usuário). Ver `trocarModoComFade`.
      trocandoModo: false,
      temporizadorDaTroca: null,
      // O modo já trocou e o corpo espera o painel novo ENTRAR pra voltar
      // (ver `aoEntrarPainelNovo`).
      aguardandoPainelNovo: false,
    }
  },
  computed: {
    // Os OCULTOS da paleta — complemento do rascunho sobre o catálogo
    // inteiro. Derivado, nunca guardado à parte: assim ele não pode divergir
    // do rascunho (um item nunca está nos dois ao mesmo tempo).
    personalizacaoDraftOcultos() {
      const visiveis = {}
      this.personalizacaoDraftVisiveis.forEach((item) => {
        visiveis[item.id] = true
      })
      return this.itensPersonalizaveisCatalogo.filter(
        (item) => !visiveis[item.id]
      )
    },
    // Há edição local pendente — mesmo critério de
    // `summary-layout-form-body.vue` (`dirty`): compara os ids/ordem do
    // rascunho contra a preferência JÁ SALVA (a prop). Comparar por JSON é
    // simples e suficiente aqui — a lista é pequena (catálogo de módulos).
    personalizacaoDirty() {
      const draft = JSON.stringify(
        this.personalizacaoDraftVisiveis.map((item) => item.id)
      )
      const salvo = JSON.stringify(
        this.itensPersonalizaveisVisiveis.map((item) => item.id)
      )
      return draft !== salvo
    },
  },
  watch: {
    // Recolher a lateral SAI do editor de personalização (descartando o
    // rascunho, como o "Cancelar"): a 64px não há canvas, paleta nem rodapé
    // que caibam — o editor virava uma coluna de texto quebrado (achado do
    // usuário ao vivo). Watcher, e não no `alternarRecolhida`, porque a
    // lateral também recolhe sozinha ao abrir um artefato (watcher
    // `artefatoAberto` do componente).
    recolhida(agora) {
      if (agora && this.modoPersonalizacao) this.aoCancelarPersonalizacao()
    },
    // Editor abriu: rearma o rascunho a partir da preferência JÁ SALVA. Só a
    // transição `false → true` importa — enquanto o editor está aberto, o
    // rascunho é dono do próprio estado (nunca pisado pela prop).
    // `immediate: true` cobre o caso raro de a sidebar nascer com o editor já
    // aberto (F5 no meio de um teste, por exemplo).
    modoPersonalizacao: {
      immediate: true,
      handler(aberto) {
        if (aberto) this.reiniciarRascunhoDePersonalizacao()
      },
    },
    // Fim do "Salvar": sucesso é o pai já ter aplicado a preferência — o
    // rascunho deixa de ser `dirty` — e aí o editor sai com o fade. No erro
    // ele segue `dirty` e o editor fica aberto (o snack diz o que houve).
    salvandoPersonalizacao(agora, antes) {
      if (antes && !agora && this.modoPersonalizacao && !this.personalizacaoDirty) {
        this.trocarModoComFade(() => this.$emit('personalizar-painel'))
      }
    },
  },
  beforeDestroy() {
    if (this.temporizadorDaTroca) clearTimeout(this.temporizadorDaTroca)
  },
  methods: {
    // As três ações do editor de personalização — todas operam no
    // RASCUNHO local, nunca direto na prop/preferência salva.
    reiniciarRascunhoDePersonalizacao() {
      this.personalizacaoDraftVisiveis = (
        this.itensPersonalizaveisVisiveis || []
      ).map((item) => ({ ...item }))
    },
    // O canvas emite `reordenar` com a lista de ids na ordem atual dele — e
    // isso cobre DOIS gestos: reordenar por drag E o drop de um tile vindo
    // da paleta (o vuedraggable insere o clone na lista e dispara o mesmo
    // `@change`). Por isso um id que ainda NÃO está no rascunho é resolvido
    // pelo catálogo, em vez de descartado. ⚠️ A primeira versão só olhava o
    // rascunho: o drop entrava, o id novo era filtrado, a prop voltava sem
    // ele e o watcher do canvas apagava o item — "o drag aparece mas o drop
    // não registra" (achado do usuário ao vivo).
    aoReordenarRascunho(ids) {
      const porId = {}
      this.itensPersonalizaveisCatalogo.forEach((item) => {
        porId[item.id] = item
      })
      this.personalizacaoDraftVisiveis.forEach((item) => {
        porId[item.id] = item
      })
      this.personalizacaoDraftVisiveis = (ids || [])
        .map((id) => porId[id])
        .filter(Boolean)
        .map((item) => ({ ...item }))
    },
    aoAdicionarAoRascunho(id) {
      if (this.personalizacaoDraftVisiveis.some((item) => item.id === id))
        return
      const item = this.itensPersonalizaveisCatalogo.find(
        (candidato) => candidato.id === id
      )
      if (!item) return
      this.personalizacaoDraftVisiveis = [
        ...this.personalizacaoDraftVisiveis,
        { ...item },
      ]
    },
    aoOcultarNoRascunho(id) {
      this.personalizacaoDraftVisiveis = this.personalizacaoDraftVisiveis.filter(
        (item) => item.id !== id
      )
    },
    // "Restaurar padrão" — o catálogo inteiro visível, na ordem original dele.
    // Só muda o RASCUNHO: ainda precisa de "Salvar" para virar preferência
    // de verdade, como o resto da edição.
    aoRestaurarPersonalizacaoPadrao() {
      this.personalizacaoDraftVisiveis = this.itensPersonalizaveisCatalogo.map(
        (item) => ({ ...item })
      )
    },
    // Toda entrada/saída do editor passa por aqui: esmaece o corpo
    // (`--trocando-modo`), espera a duração do fade, só ENTÃO roda a ação
    // que troca o modo (emit ao pai), e só tira a classe quando o painel novo
    // ENTRA (`aoEntrarPainelNovo`, com a rede `REDE_DA_TROCA_DE_MODO_MS`).
    // Reentrância barrada: um segundo clique durante o fade é ignorado.
    trocarModoComFade(acao) {
      if (this.trocandoModo) return
      this.trocandoModo = true
      this.temporizadorDaTroca = setTimeout(() => {
        acao()
        // ⚠️ NÃO `$nextTick`: ele roda antes de o browser pintar, então a
        // classe caía no MESMO quadro em que o conteúdo novo entrava — sem
        // um quadro pintado em opacity 0, não há de onde animar, e o corpo
        // simplesmente aparecia (pop). E um timer fixo também não serve: o
        // `out-in` interno remove o painel antigo dois `rAF` depois da troca
        // e só ENTÃO insere o novo (mais um `rAF`) — com 50ms o corpo voltava
        // num quadro em que o painel de edição ainda estava lá e "Recentes"
        // já tinha montado colado nele (frame do usuário). Quem sabe quando
        // o painel novo está de fato no lugar é o `@after-enter` da
        // `<transition>` — `aoEntrarPainelNovo`. O timer abaixo é só rede:
        // se o hook não vier (painel que não trocou), o corpo volta mesmo
        // assim.
        this.aguardandoPainelNovo = true
        this.temporizadorDaTroca = setTimeout(
          this.concluirTrocaDeModo,
          REDE_DA_TROCA_DE_MODO_MS
        )
      }, DURACAO_DA_TROCA_DE_MODO_MS)
    },
    aoEntrarPainelNovo() {
      if (!this.aguardandoPainelNovo) return
      // O `after-enter` chega no `rAF` em que o painel entrou; mais um quadro
      // garante que ele foi PINTADO invisível antes de a classe cair — é o
      // quadro de origem do fade-in.
      requestAnimationFrame(this.concluirTrocaDeModo)
    },
    concluirTrocaDeModo() {
      if (!this.aguardandoPainelNovo) return
      this.aguardandoPainelNovo = false
      clearTimeout(this.temporizadorDaTroca)
      this.trocandoModo = false
    },
    // Recolhida, o botão primeiro ABRE a lateral (o editor não cabe em 64px —
    // abrir o editor direto ali bugava a coluna, achado do usuário) e só
    // então entra no modo de edição, já com a largura final.
    alternarPersonalizacaoComFade() {
      if (this.recolhida && !this.modoPersonalizacao) {
        this.alternarRecolhida()
      }
      this.trocarModoComFade(() => this.$emit('personalizar-painel'))
    },
    // "Cancelar" — descarta o rascunho e fecha o editor. Nada é persistido.
    aoCancelarPersonalizacao() {
      this.trocarModoComFade(() => {
        this.reiniciarRascunhoDePersonalizacao()
        this.$emit('personalizar-painel')
      })
    },
    // "Salvar" — emite na hora (sem fade: o botão mostra o `loading` do
    // pai enquanto o PUT/DELETE voa); quem fecha o editor é o watcher de
    // `salvandoPersonalizacao`. Esta sidebar não sabe de API nem de storage.
    aoSalvarPersonalizacao() {
      if (this.salvandoPersonalizacao || this.trocandoModo) return
      const ids = this.personalizacaoDraftVisiveis.map((item) => item.id)
      this.$emit('salvar-personalizacao', ids)
    },
  },
}
